import React, { forwardRef } from 'react';
import { Upload, Image, Sparkles, Layout } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { DropzoneRootProps } from 'react-dropzone';

interface DropZoneProps extends DropzoneRootProps {
  isDragActive: boolean;
  isEmpty: boolean;
}

export const DropZone = forwardRef<HTMLDivElement, DropZoneProps>(
  ({ isDragActive, isEmpty, children, ...rootProps }, ref) => {
    return (
      <div
        {...rootProps}
        ref={ref}
        className={cn(
          'flex-1 relative min-h-[calc(100vh-8rem)]', // Ensure minimum height
          isDragActive && 'bg-blue-50'
        )}
        onClick={e => e.preventDefault()} // Prevent click handling
      >
        {/* Drop zone overlay - only visible when dragging */}
        {isDragActive && (
          <div className="absolute inset-0 border-2 border-blue-500 border-dashed rounded-lg flex items-center justify-center bg-blue-50/80 backdrop-blur-sm z-10">
            <div className="text-center transform scale-110 transition-transform duration-200">
              <div className="relative inline-block mb-6">
                <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <Upload size={40} className="text-blue-600" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center transform rotate-12">
                  <Sparkles size={16} className="text-yellow-600" />
                </div>
                <div className="absolute -bottom-2 -left-2 w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center transform -rotate-12">
                  <Image size={16} className="text-green-600" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-blue-700 mb-2 font-sora">
                Drop to Add Images
              </h3>
              <p className="text-blue-600">Release your files to start uploading</p>
            </div>
          </div>
        )}

        {/* Empty state */}
        {isEmpty && !isDragActive && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center max-w-lg mx-auto px-6">
              <div className="relative inline-block mb-8">
                {/* Main icon */}
                <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg transform transition-transform hover:scale-105">
                  <Layout className="w-12 h-12 text-white" />
                </div>
                {/* Decorative elements */}
                <div className="absolute -top-3 -right-3 w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center transform rotate-12">
                  <Sparkles className="w-5 h-5 text-yellow-600" />
                </div>
                <div className="absolute -bottom-3 -left-3 w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center transform -rotate-12">
                  <Image className="w-5 h-5 text-green-600" />
                </div>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mb-4 font-sora">
                Create Your Moodboard
              </h2>

              <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                Drag and drop images here to build your visual inspiration. Add photos, designs, and
                artwork to your moodboard.
              </p>

              <div className="text-sm text-gray-500 flex items-center justify-center gap-2">
                <span>Supports</span>
                <span className="px-2 py-1 bg-gray-100 rounded-md">JPG</span>
                <span className="px-2 py-1 bg-gray-100 rounded-md">PNG</span>
                <span className="px-2 py-1 bg-gray-100 rounded-md">GIF</span>
                <span className="px-2 py-1 bg-gray-100 rounded-md">WebP</span>
              </div>
            </div>
          </div>
        )}

        {children}
      </div>
    );
  }
);

DropZone.displayName = 'DropZone';
